import { useMemo } from 'react';
import { Box, Typography, Skeleton, Alert } from '@mui/material';
import ImageGallery from '@/features/opportunities/form/ImageGallery';
import DropzoneUploader from '@/components/form/DropzoneUploader';
import { useGetOpportunityMedia } from '@/lib/api/opportunities';
import {
  useGetInvestmentMedia,
  useUploadInvestmentMedia,
  useDeleteInvestmentMedia,
} from '@/lib/api/investments';

interface MediaTabProps {
  opportunityId?: string;
  investmentId?: string;
  canEdit?: boolean;
}

export const MediaTab = ({ opportunityId, investmentId, canEdit = false }: MediaTabProps) => {
  const opportunityMediaQuery = useGetOpportunityMedia(investmentId ? undefined : opportunityId);
  const investmentMediaQuery = useGetInvestmentMedia(investmentId);
  const { mutateAsync: uploadInvestmentMedia } = useUploadInvestmentMedia();
  const { mutateAsync: deleteInvestmentMedia } = useDeleteInvestmentMedia();

  const { data: media = [], isLoading } = investmentId
    ? investmentMediaQuery
    : opportunityMediaQuery;

  const images = useMemo(
    () =>
      media
        ?.filter((m) => m.type === 'IMAGE')
        .map((m) => ({
          id: m.id,
          type: m.mimeType,
          url: m.signedUrl,
          name: m.fileName,
        })) ?? [],
    [media]
  );

  if (isLoading) {
    return <Skeleton variant="rectangular" height={300} />;
  }

  if (!investmentId || !canEdit) {
    const viewMedia = media.filter(
      (m) => m.mimeType.startsWith('image/') || m.mimeType.startsWith('video/')
    );

    return (
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Imágenes y vídeos
        </Typography>
        {viewMedia.length === 0 ? (
          <Alert severity="info">No hay imágenes o vídeos disponibles.</Alert>
        ) : (
          <ImageGallery
            files={viewMedia.map((m) => ({
              url: m.signedUrl,
              mimeType: m.mimeType,
              name: m.fileName,
            }))}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Imágenes y vídeos
      </Typography>
      <DropzoneUploader
        existingFiles={images}
        handleUploadFile={async (file) =>
          uploadInvestmentMedia({
            file,
            investmentId,
            fileType: 'IMAGE',
          })
        }
        maxFiles={30}
        uploadType="images"
        onDeleteExistingFile={(id) => deleteInvestmentMedia(id)}
      />
    </Box>
  );
};
